import { memo, useMemo, useState, Fragment } from 'react';
import { Box, Button, Flex, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthContext } from 'context/authContext';
import { STATUS } from 'projectConstants';
import { ExportDataRequestBody } from 'models/requestBody/createExportData.interface';
import {
  getBlockDetails,
  getDimension,
  getIndicatorTableStatus,
  getModelDetails,
  overallIndicatorFilter,
} from 'redux/PlanPageSlice';
import {
  getBaseScenario,
  getCompareTableFilter,
  getExpandKey,
  getTableData,
  getTableHeaderData,
  getVarianceFilterTable,
  setCompareTableFilter,
  setExpandKey,
  toggleVarianceFilterTable,
} from 'redux/ScenarioSlice';
import apiService from 'services';
import saveFile, { buildBlockExportFileName } from 'utils/file';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_CARD_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_CELL_PX,
  PLAN_TABLE_CELL_PY,
  PLAN_TABLE_HEADER_BG,
  PLAN_TABLE_STICKY_BG,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

import PlanCard from './PlanCard';
import PlanCardToolbarStrip from './PlanCardToolbarStrip';
import PlanCompareDataTableRow from './PlanCompareDataTableRow';
import PlanDataTableBodySkeleton from './PlanDataTableBodySkeleton';

import type { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanCompareDataTable = ({ onToggleAuthModal, pageScrollMode = false }: Properties) => {
  const { blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const tableData = useSelector(getTableData);
  const tableHeaderData = useSelector(getTableHeaderData);
  const tableFilter = useSelector(getCompareTableFilter);
  const expandKey = useSelector(getExpandKey);
  const varianceFilter = useSelector(getVarianceFilterTable);
  const modelDetail = useSelector(getModelDetails);
  const blockDetails = useSelector(getBlockDetails);
  const dimension = useSelector(getDimension);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const baseScenario = useSelector(getBaseScenario);
  const compareTableStatus = useSelector(getIndicatorTableStatus);
  const [isExporting, setIsExporting] = useState(false);

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const visibleHeaders = (tableHeaderData || []).filter((item: any) => item?.isShow);

  const onExpandHeader = (header: any) => {
    if (!header?.isShowExpandable || timeAggregation === 'Y') return;
    if (expandKey !== header.name && (tableFilter === 'Y' || tableFilter === 'Q')) {
      dispatch(setExpandKey(header.name));
    } else {
      dispatch(setExpandKey(''));
    }
  };

  const exportRequestBody: ExportDataRequestBody = useMemo(
    () => ({
      orient: 'table',
      query_params: {
        rows: [{ name: 'Indicators', filter: indicatorFilter }, { name: dimension?.name || null }],
        columns: [{ name: 'Time', display_levels: [tableFilter] }],
        filters: [],
      },
      scenario_id: baseScenario?.id,
    }),
    [baseScenario?.id, dimension?.name, indicatorFilter, tableFilter],
  );

  const { refetch: fetchExportData } = useQuery(
    ['compare-export', blockId, baseScenario?.id, tableFilter],
    () =>
      apiService.create_block_export({
        block_id: Number(blockId),
        request_body: exportRequestBody,
      }),
    {
      enabled: false,
      onError: (response: any) => {
        if (response.response?.data?.message) {
          navigate('/error');
        }
      },
    },
  );

  const onDownload = async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const { data: exportData } = await fetchExportData();
      if (exportData) {
        const fileName = buildBlockExportFileName(blockDetails?.name);
        const blob = [exportData.data];
        const contentType =
          exportData.headers?.['content-type'] ??
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        saveFile(fileName, blob, contentType);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const onTimeFilterChange = (filterKey: PlanTimeFilterValue) => {
    dispatch(setCompareTableFilter(filterKey));
  };

  const isTableLoading =
    compareTableStatus === STATUS.LOADING ||
    (compareTableStatus === STATUS.IDLE && !tableHeaderData);
  const hasCachedTable = Boolean((tableData as unknown[])?.length);
  const showTableSkeleton = isTableLoading && !hasCachedTable;
  const isTableEmpty =
    compareTableStatus === STATUS.SUCCESS && (!tableData || (tableData as unknown[]).length === 0);

  const visibleRows = (tableData as any[]) || [];

  const varianceToggle = (
    <Button
      size='sm'
      h='28px'
      minH='28px'
      w='28px'
      minW='28px'
      p={0}
      {...PLAN_TEXT.labelSmMedium}
      borderRadius='8px'
      border='1px solid'
      borderColor={varianceFilter ? accent.solid : neutral.border}
      bg={neutral.surface}
      color={accent.solid}
      _hover={{ borderColor: accent.solid }}
      onClick={() => dispatch(toggleVarianceFilterTable())}
    >
      %
    </Button>
  );

  const cardFlex = pageScrollMode ? '1 1 auto' : '1 1 0';
  const cardMinH = pageScrollMode ? undefined : 0;

  if (showTableSkeleton) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <PlanCardToolbarStrip
          hint='Expand the table to drill into the values for each of your items'
          showTimeToggle={timeAggregation !== 'Y'}
          timeValue={tableFilter as PlanTimeFilterValue}
          onTimeChange={onTimeFilterChange}
          onDownload={onDownload}
          isExporting={isExporting}
          leadingActions={varianceToggle}
        />
        <Box
          flex={pageScrollMode ? undefined : '1'}
          minH={pageScrollMode ? undefined : 0}
          minW={0}
          p={PLAN_CARD_PADDING}
        >
          <Box
            border='1px solid'
            borderColor={PLAN_TABLE_CELL_BORDER}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            overflow='hidden'
          >
            <PlanDataTableBodySkeleton columnCount={5} />
          </Box>
        </Box>
      </PlanCard>
    );
  }

  if (!tableHeaderData || isTableEmpty) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <Flex h='100%' minH='200px' alignItems='center' justifyContent='center'>
          <Text {...PLAN_TEXT.body} color={textToken.secondary} textAlign='center'>
            No compare table data available for the current filters.
          </Text>
        </Flex>
      </PlanCard>
    );
  }

  return (
    <PlanCard flex={cardFlex} minH={cardMinH}>
      <PlanCardToolbarStrip
        hint='Expand the table to drill into the values for each of your items'
        showTimeToggle={timeAggregation !== 'Y'}
        timeValue={tableFilter as PlanTimeFilterValue}
        onTimeChange={onTimeFilterChange}
        onDownload={onDownload}
        isExporting={isExporting}
        leadingActions={varianceToggle}
      />

      <Box
        flex={pageScrollMode ? undefined : '1'}
        minH={pageScrollMode ? undefined : 0}
        minW={0}
        p={PLAN_CARD_PADDING}
        display='flex'
        flexDirection='column'
      >
        <Box
          flex={pageScrollMode ? undefined : '1'}
          minH={pageScrollMode ? undefined : 0}
          minW={0}
          border='1px solid'
          borderColor={PLAN_TABLE_CELL_BORDER}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          overflowX='auto'
          overflowY={pageScrollMode ? 'visible' : 'auto'}
          className={pageScrollMode ? undefined : 'scroll'}
          position='relative'
        >
          <Table size='sm' variant='unstyled'>
            <Thead position='sticky' top={0} zIndex={2}>
              <Tr>
                <Th
                  position='sticky'
                  left={0}
                  zIndex={3}
                  bg={PLAN_TABLE_STICKY_BG}
                  py={PLAN_TABLE_CELL_PY}
                  px={PLAN_TABLE_CELL_PX}
                  minW={PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH}
                  borderBottom='1px solid'
                  borderRight='1px solid'
                  borderColor={PLAN_TABLE_CELL_BORDER}
                  {...PLAN_TEXT.tableComfortaa}
                  color={textToken.primary}
                  textTransform='none'
                  letterSpacing='normal'
                ></Th>
                {visibleHeaders.map((item: any, index: number) => {
                  const isExpanded = expandKey === item?.name;
                  const isExpandable = Boolean(item?.isShowExpandable) && timeAggregation !== 'Y';
                  return (
                    <Fragment key={`${item?.name ?? ''}-${index}`}>
                      <Th
                        key={`${item?.name ?? ''}-value-${index}`}
                        py={PLAN_TABLE_CELL_PY}
                        px={PLAN_TABLE_CELL_PX}
                        bg={PLAN_TABLE_HEADER_BG}
                        borderBottom='1px solid'
                        borderRight='1px solid'
                        borderColor={PLAN_TABLE_CELL_BORDER}
                        {...PLAN_TEXT.tableComfortaa}
                        color={textToken.primary}
                        textTransform='none'
                        letterSpacing='normal'
                        textAlign='end'
                        whiteSpace='nowrap'
                        cursor={isExpandable ? 'pointer' : 'default'}
                        onClick={() => onExpandHeader(item)}
                      >
                        <Flex alignItems='center' justifyContent='flex-end' gap={1}>
                          {isExpandable &&
                            (isExpanded ? (
                              <ChevronRightIcon boxSize={3} color={accent.solid} />
                            ) : (
                              <ChevronLeftIcon boxSize={3} color={accent.solid} />
                            ))}
                          {item?.name}
                        </Flex>
                      </Th>
                      <Th
                        key={`${item?.name ?? ''}-diff-${index}`}
                        py={PLAN_TABLE_CELL_PY}
                        px={PLAN_TABLE_CELL_PX}
                        bg={PLAN_TABLE_HEADER_BG}
                        borderBottom='1px solid'
                        borderRight='1px solid'
                        borderColor={PLAN_TABLE_CELL_BORDER}
                        {...PLAN_TEXT.labelSmMedium}
                        color={textToken.primary}
                        textTransform='none'
                        textAlign='end'
                      >
                        Diff
                      </Th>
                    </Fragment>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {visibleRows.map((row: any) => (
                <PlanCompareDataTableRow key={row.id ?? row.name} row={row} expandKey={expandKey} />
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </PlanCard>
  );
};

export default memo(PlanCompareDataTable);
